import { useNavigate } from '@tanstack/react-router';
import { InlineNote, TopBar, useNote } from '../components/ui';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { useApp } from '../lib/app';
import { friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { MAX_PLACES, removePlace } from '../lib/savedPlaces';
import type { SavedPlace } from '../lib/types';

/** Ghar, Kaam and up to three more named places (the server keeps 5) — set, change or remove. */
export function SavedPlaces() {
  const { api, rider, setRider } = useApp();
  const [note, say] = useNote();
  const t = useT();
  const nav = useNavigate();
  const places = rider?.places ?? [];
  const others = places.filter((p) => p.label === 'OTHER');

  const remove = async (p: SavedPlace) => {
    try {
      setRider(await api.updateMe({ places: removePlace(places, p) }));
      say(t('places.removed', { name: p.name }), 'good');
    } catch (e) {
      say(friendly(e, t), 'error');
    }
  };

  const row = (icon: 'home' | 'briefcase' | 'pin', name: string, p: SavedPlace | undefined, onSet: () => void, first: boolean) => (
    <div key={`${icon}-${name}`} data-testid="saved-place" style={{ height: 64, padding: '0 8px 0 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: first ? undefined : 'inset 0 1px 0 var(--line)' }}>
      <Icon name={icon} size={20} style={{ color: 'var(--ink2)' }} />
      <button type="button" onClick={onSet} style={{ flex: 1, minWidth: 0, height: 60, border: 0, background: 'transparent', textAlign: 'left', color: 'var(--ink)', cursor: 'pointer', padding: 0 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{p?.name ?? name}</span>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p?.address ?? t('places.tap')}</span>
      </button>
      {p && (
        <button
          type="button"
          aria-label={t('common.removeX', { name: p.name })}
          onClick={() => void remove(p)}
          style={{ width: 44, height: 44, border: 0, background: 'transparent', color: 'var(--ink2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Icon name="trash" size={18} />
        </button>
      )}
    </div>
  );

  return (
    <Screen name="account" label={t('places.title')}>
      <div className="zr-scroll" style={{ padding: 'calc(var(--sat) + 10px) 20px calc(var(--sab) + 16px)' }}>
        <TopBar onBack={() => nav({ to: '/account' })} title={t('places.title')} style={{ padding: 0 }} />
        <div className="zr-group zr-stagger" style={{ marginTop: 22 }}>
          {row('home', t('home.home'), places.find((x) => x.label === 'HOME'), () => nav({ to: '/search', search: { field: 'home' } }), true)}
          {row('briefcase', t('home.work'), places.find((x) => x.label === 'WORK'), () => nav({ to: '/search', search: { field: 'work' } }), false)}
          {others.map((p) => row('pin', p.name, p, () => undefined, false))}
        </div>
        {places.length < MAX_PLACES && (
          <button
            type="button"
            onClick={() => nav({ to: '/search', search: { field: 'place' } })}
            className="zr-press-soft"
            style={{ marginTop: 12, width: '100%', height: 56, border: 0, borderRadius: 18, boxShadow: 'inset 0 0 0 1.5px var(--line2)', background: 'transparent', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
          >
            <Icon name="plus" size={18} sw={2.3} />
            {t('places.add')}
          </button>
        )}
        <InlineNote note={note} />
        <p style={{ margin: '12px 2px 0', fontSize: 13, fontWeight: 500, color: 'var(--ink3)' }}>{t('places.max')}</p>
      </div>
    </Screen>
  );
}
